import Image from 'next/image';

interface ResponsiveImageProps {
  src: string;
  alt: string;
  fill?: boolean;
  width?: number;
  height?: number;
  priority?: boolean;
  loading?: 'lazy' | 'eager';
  className?: string;
  title?: string;
  sizes?: string;
}

export function ResponsiveImage({ 
  src, 
  alt,
  fill = false,
  width,
  height,
  priority = false,
  loading: propLoading,
  className = '',
  title,
  sizes: propSizes
}: ResponsiveImageProps) {
  // Check if the image is an SVG
  const isSvg = src.toLowerCase().endsWith('.svg');
  
  if (isSvg) {
    return (
      <div className={`relative ${fill ? 'h-full' : ''}`}>
        <Image
          src={src}
          alt={alt}
          fill={fill}
          width={!fill ? width : undefined}
          height={!fill ? height : undefined}
          className={className}
          priority={priority}
          loading={!priority ? propLoading : undefined}
          title={title}
        />
      </div>
    );
  }

  // Remove file extension to add size suffixes
  const baseSrc = src.replace(/\.[^/.]+$/, '');
  
  // Use provided sizes or calculate based on layout
  const sizes = propSizes || (fill 
    ? '100vw' 
    : width 
      ? `(max-width: 640px) ${Math.min(width, 640)}px, (max-width: 1024px) ${Math.min(width, 1024)}px, ${width}px`
      : '(max-width: 640px) 100vw, (max-width: 1024px) 100vw, 100vw');

  return (
    <div className={`relative ${fill ? 'h-full' : ''}`}>
      <picture>
        {/* AVIF format */}
        <source
          type="image/avif"
          srcSet={`
            ${baseSrc}-sm.avif 640w,
            ${baseSrc}-md.avif 1024w,
            ${baseSrc}.avif 1920w
          `}
          sizes={sizes}
        />
        {/* WebP format */}
        <source
          type="image/webp"
          srcSet={`
            ${baseSrc}-sm.webp 640w,
            ${baseSrc}-md.webp 1024w,
            ${baseSrc}.webp 1920w
          `}
          sizes={sizes}
        />
        {/* JPEG format */}
        <source
          type="image/jpeg"
          srcSet={`
            ${baseSrc}-sm.jpg 640w,
            ${baseSrc}-md.jpg 1024w,
            ${src} 1920w
          `}
          sizes={sizes}
        />
        {/* Fallback image */}
        <Image
          src={src}
          alt={alt}
          fill={fill}
          width={!fill ? width : undefined}
          height={!fill ? height : undefined}
          className={`${className} ${fill ? 'object-cover' : ''}`}
          priority={priority}
          loading={!priority ? propLoading : undefined}
          sizes={sizes}
          title={title}
        />
      </picture>
    </div>
  );
} 